import { useState } from 'react';

interface KQLCodeBlockProps {
  code: string;
  onCopy?: () => void;
  explanationText?: string;
}

// KQL keywords for syntax highlighting
const KQL_KEYWORDS = new Set([
  'let', 'where', 'project', 'extend', 'summarize', 'sort', 'by', 'and', 'or', 'not',
  'true', 'false', 'ago', 'now', 'bin', 'round', 'sum', 'count', 'avg', 'max', 'min',
  'contains', 'startswith', 'make-series', 'mv-expand', 'render', 'union', 'find',
  'tostring', 'split', 'strcat', 'sumif', 'startofday', 'between', 'typeof',
  'project-away', 'in', 'distinct', 'order', 'top', 'take', 'limit', 'join',
  'parse', 'extract', 'toint', 'tolong', 'todouble', 'tolower', 'toupper',
  'withsource', 'default', 'step', 'from', 'to', 'on', 'range',
  'series_decompose_anomalies', 'abs', 'series', 'print', 'evaluate',
]);

type TokenType = 'keyword' | 'string' | 'number' | 'comment' | 'operator' | 'placeholder' | 'text';
interface Token { type: TokenType; value: string }

function tokenize(code: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < code.length) {
    // Placeholder {{VAR}}
    if (code[i] === '{' && code[i + 1] === '{') {
      const end = code.indexOf('}}', i + 2);
      if (end !== -1) {
        tokens.push({ type: 'placeholder', value: code.slice(i, end + 2) });
        i = end + 2;
        continue;
      }
    }

    // Comment //
    if (code[i] === '/' && code[i + 1] === '/') {
      const end = code.indexOf('\n', i);
      const s = end === -1 ? code.slice(i) : code.slice(i, end);
      tokens.push({ type: 'comment', value: s });
      i += s.length;
      continue;
    }

    // Double-quoted string
    if (code[i] === '"') {
      let j = i + 1;
      while (j < code.length && code[j] !== '"' && code[j] !== '\n') j++;
      tokens.push({ type: 'string', value: code.slice(i, j + 1) });
      i = j + 1;
      continue;
    }

    // Single-quoted string
    if (code[i] === "'") {
      let j = i + 1;
      while (j < code.length && code[j] !== "'" && code[j] !== '\n') j++;
      tokens.push({ type: 'string', value: code.slice(i, j + 1) });
      i = j + 1;
      continue;
    }

    // Number (including decimal)
    if (/[0-9]/.test(code[i]) && (i === 0 || !/[a-zA-Z_]/.test(code[i - 1]))) {
      let j = i;
      while (j < code.length && /[0-9.e]/.test(code[j])) j++;
      tokens.push({ type: 'number', value: code.slice(i, j) });
      i = j;
      continue;
    }

    // Identifier or keyword (allow hyphen mid-word for make-series etc)
    if (/[a-zA-Z_]/.test(code[i])) {
      let j = i;
      while (j < code.length && /[a-zA-Z0-9_\-]/.test(code[j])) {
        // Stop hyphen at end of word boundary (avoid eating operators)
        if (code[j] === '-' && (j + 1 >= code.length || !/[a-zA-Z_]/.test(code[j + 1]))) break;
        j++;
      }
      const word = code.slice(i, j);
      const isKw = KQL_KEYWORDS.has(word.toLowerCase());
      tokens.push({ type: isKw ? 'keyword' : 'text', value: word });
      i = j;
      continue;
    }

    // Operator-like chars
    if (/[|=<>!+\-*\/()[\]{},.]/.test(code[i])) {
      tokens.push({ type: 'operator', value: code[i] });
      i++;
      continue;
    }

    tokens.push({ type: 'text', value: code[i] });
    i++;
  }

  return tokens;
}

const TOKEN_COLORS: Record<TokenType, string> = {
  keyword:     '#4fc1ff',
  string:      '#ce9178',
  number:      '#b5cea8',
  comment:     '#6a9955',
  operator:    '#d4d4d4',
  placeholder: 'placeholder', // special — rendered as badge
  text:        '#d4d4d4',
};

function buildCommentedQuery(code: string, explanationText: string): string {
  return `// ${explanationText.split('\n').join('\n// ')}\n\n${code}`;
}

export default function KQLCodeBlock({ code, onCopy, explanationText }: KQLCodeBlockProps) {
  const [copied, setCopied] = useState<'clean' | 'commented' | null>(null);

  const copy = (type: 'clean' | 'commented') => {
    const text = type === 'commented' && explanationText
      ? buildCommentedQuery(code, explanationText)
      : code;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      onCopy?.();
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const tokens = tokenize(code);

  return (
    <div className="relative group rounded-lg overflow-hidden" style={{ border: '1px solid var(--outline-variant)', backgroundColor: '#1e1e1e' }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2" style={{ backgroundColor: '#2a2a2a', borderBottom: '1px solid var(--outline-variant)' }}>
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>KQL</span>
        <div className="flex items-center gap-2">
          {explanationText && (
            <button
              onClick={() => copy('commented')}
              className="text-[11px] transition-colors px-2 py-1 rounded"
              style={{ color: 'var(--text-tertiary)' }}
            >
              {copied === 'commented' ? '✓ Copied with comments' : 'Copy with comments'}
            </button>
          )}
          <button
            onClick={() => copy('clean')}
            className={[
              'text-[12px] font-semibold px-3 py-1 rounded transition-all',
              copied === 'clean'
                ? 'bg-[#107c10] text-white'
                : 'bg-[#0078d4] hover:bg-[#106ebe] text-white',
            ].join(' ')}
          >
            {copied === 'clean' ? '✓ Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Code */}
      <pre className="p-4 overflow-x-auto text-[13px] leading-6 font-mono m-0 whitespace-pre">
        <code>
          {tokens.map((tok, idx) => {
            if (tok.type === 'placeholder') {
              const varName = tok.value.slice(2, -2);
              return (
                <span
                  key={idx}
                  className="inline-flex items-center px-1.5 py-0 rounded text-[#0078d4] bg-[#0078d4]/10 border border-[#0078d4]/30 text-[12px] leading-5 mx-0.5 font-sans font-semibold"
                >
                  {varName}
                </span>
              );
            }
            return (
              <span key={idx} style={{ color: TOKEN_COLORS[tok.type] }}>
                {tok.value}
              </span>
            );
          })}
        </code>
      </pre>
    </div>
  );
}
